import React from "react";
import SEO from "../components/SEO";

const SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    body: [
      "By accessing and using the Barbequeen website (barbequeen.com) or any of our services, you confirm that you have read, understood, and agree to be bound by these Terms & Conditions and our Privacy Policy.",
      "If you do not agree to these terms, please do not use our website or services.",
    ],
  },
  {
    title: "2. About Us",
    body: [
      "Barbequeen is a restaurant brand operated by Nonvee Foods Private Limited, with its registered office at # 32, Valmiki Street, Near Ramakrishna Higher Secondary School, Subramaniya Nagar, Salem – 636 005, Tamil Nadu, India.",
      'References to "Barbequeen", "we", "us", or "our" in these Terms refer to Nonvee Foods Private Limited.',
    ],
  },
  {
    title: "3. Use of the Website",
    body: [
      "You agree to use this website only for lawful purposes and in a manner that does not infringe the rights of others. You must not:",
      "• Use the website in any way that is unlawful, fraudulent, or harmful.",
      "• Transmit any unsolicited or unauthorised advertising material.",
      "• Attempt to gain unauthorised access to any part of our website or systems.",
      "• Reproduce, duplicate, copy, or exploit any part of our website without our express written permission.",
    ],
  },
  {
    title: "4. Reservations & Orders",
    body: [
      "Reservations made through our website are subject to availability. We reserve the right to accept or decline any reservation at our discretion.",
      "All online orders are subject to our confirmation. Prices displayed on the website are inclusive of applicable taxes unless stated otherwise and may be subject to change without notice.",
      "In the event of pricing errors, we reserve the right to cancel the affected orders and notify you accordingly.",
    ],
  },
  {
    title: "5. Intellectual Property",
    body: [
      "All content on this website — including text, graphics, logos, images, videos, and software — is the property of Nonvee Foods Private Limited or its licensors and is protected by applicable intellectual property laws.",
      "You may not reproduce, distribute, modify, or create derivative works of any content without our prior written consent.",
    ],
  },
  {
    title: "6. Limitation of Liability",
    body: [
      "To the fullest extent permitted by law, Barbequeen shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, this website or our services.",
      "We do not warrant that the website will be uninterrupted, error-free, or free of viruses or other harmful components. You are responsible for implementing sufficient safeguards for your own use of the internet.",
    ],
  },
  {
    title: "7. Third-Party Links",
    body: [
      "Our website may contain links to third-party websites for your convenience. These links do not signify our endorsement of those websites. We have no control over the content of those sites and accept no responsibility for them or for any loss or damage that may arise from your use of them.",
    ],
  },
  {
    title: "8. Disclaimer of Warranties",
    body: [
      'This website and its content are provided on an "as is" and "as available" basis without any warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.',
      "We do not warrant the accuracy, completeness, or usefulness of any information on the site.",
    ],
  },
  {
    title: "9. Governing Law",
    body: [
      "These Terms & Conditions shall be governed by and construed in accordance with the laws of India. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts in Salem, Tamil Nadu, India.",
    ],
  },
  {
    title: "10. Amendments",
    body: [
      "We reserve the right to revise these Terms & Conditions at any time. Changes will be posted on this page with an updated effective date. Your continued use of the website after any changes constitutes your acceptance of the new Terms.",
      "We encourage you to review these Terms periodically.",
    ],
  },
  {
    title: "11. Contact Information",
    body: [
      "If you have any questions about these Terms & Conditions, please contact us:",
      "Nonvee Foods Private Limited",
      "# 32, Valmiki Street, Near Ramakrishna Higher Secondary School,",
      "Subramaniya Nagar, Salem – 636 005.",
      "Email: info@barbequeen.com",
      "Phone: +91 95972 28788",
    ],
  },
];

const TermsAndConditionsPage = () => {
  const lastUpdated = "15 July 2026";

  return (
    <div
      className="min-h-screen bg-[#f9f5f0]"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <SEO
        title="Terms & Conditions | Barbequeen Restaurant"
        metaTitle="Terms & Conditions | Barbequeen Restaurant"
        description="Review the Terms & Conditions governing the use of Barbequeen Restaurant website, reservations, ordering, and services."
        keywords="Barbequeen terms and conditions, terms of service, legal Barbequeen"
        robots="index, follow"
        canonical="https://barbequeen.com/terms-and-conditions"
        og={{
          type: "website",
          url: "https://barbequeen.com/terms-and-conditions",
          title: "Terms & Conditions | Barbequeen Restaurant",
          description:
            "Review the Terms & Conditions governing the use of Barbequeen Restaurant website and services.",
          image: "https://barbequeen.com/images/home-banner.jpg",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN",
        }}
      />

      {/* Hero Banner */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: "320px" }}
      >
        <img
          src="https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=1600&auto=format&fit=crop"
          alt="Terms and Conditions"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,0,2,0.75) 0%, rgba(10,0,2,0.88) 100%)",
          }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
          <p className="text-xs font-bold tracking-[0.5em] uppercase text-[#c0424e] mb-4">
            Legal
          </p>
          <h1
            className="text-4xl sm:text-6xl font-bold text-white uppercase leading-tight tracking-wide mb-5"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Terms &amp; Conditions
          </h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto leading-relaxed">
            Please read these terms carefully before using our website or
            services.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 lg:px-8 py-16">
        {/* Intro card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#9b1c2e]/10 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-6 h-6 text-[#9b1c2e]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
            </div>
            <div>
              <p className="text-gray-700 leading-relaxed text-sm">
                These Terms &amp; Conditions govern your use of the Barbequeen
                website and services provided by{" "}
                <strong>Nonvee Foods Private Limited</strong>. By accessing our
                website you agree to be bound by these terms.
              </p>
              <p className="mt-3 text-xs text-gray-400">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {SECTIONS.map((section) => (
            <div
              key={section.title}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-1 h-6 bg-[#9b1c2e] rounded-full" />
                <h2
                  className="text-lg font-bold text-gray-900"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {section.title}
                </h2>
              </div>
              <div className="space-y-3">
                {section.body.map((para, i) => (
                  <p key={i} className="text-sm text-gray-600 leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default TermsAndConditionsPage;
